/**
 * SupportTab - Panou pentru vizualizarea întrebărilor și erorilor de suport
 * Disponibil doar pentru Superadmin (Master)
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  MessageCircle, AlertTriangle, CheckCircle, Clock, 
  Building2, User, Calendar, RefreshCw, Check, X,
  HelpCircle, AlertOctagon, Reply, Send, Mail
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SupportTab() {
  const [questions, setQuestions] = useState([]);
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("errors");
  
  // Reply dialog state
  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [questionsRes, errorsRes, statsRes] = await Promise.all([
        axios.get(`${API}/support/questions`),
        axios.get(`${API}/support/errors`),
        axios.get(`${API}/support/stats`)
      ]);
      
      setQuestions(questionsRes.data.questions || []);
      setErrors(errorsRes.data.errors || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching support data:", error);
      toast.error("Eroare la încărcarea datelor de suport");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markErrorResolved = async (errorId) => {
    try {
      await axios.patch(`${API}/support/error/${errorId}?resolved=true`);
      toast.success("Eroare marcată ca rezolvată");
      fetchData();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  const openReplyDialog = (question) => {
    setSelectedQuestion(question);
    setReplyText("");
    setShowReplyDialog(true);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error("Scrie un răspuns înainte de a trimite");
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(`${API}/support/questions/${selectedQuestion.id}/reply`, {
        reply: replyText.trim(),
        question_id: selectedQuestion.id
      });
      
      toast.success("Răspuns trimis cu succes!");
      setShowReplyDialog(false);
      setSelectedQuestion(null);
      setReplyText("");
      fetchData();
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error(error.response?.data?.detail || "Eroare la trimiterea răspunsului");
    } finally {
      setSendingReply(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingErrors = errors.filter(e => !e.resolved);
  const pendingQuestions = questions.filter(q => q.needs_followup && !q.resolved);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <AlertOctagon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{pendingErrors.length}</p>
                <p className="text-xs text-red-600">Erori în așteptare</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{pendingQuestions.length}</p>
                <p className="text-xs text-amber-600">Întrebări fără răspuns</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats?.errors?.resolved || 0}</p>
                <p className="text-xs text-green-600">Erori rezolvate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats?.questions?.total || 0}</p>
                <p className="text-xs text-blue-600">Total întrebări</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Centru Suport & Erori
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Reîmprospătează
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="errors" className="flex items-center gap-2">
                <AlertOctagon className="w-4 h-4" />
                Erori ({errors.length})
                {pendingErrors.length > 0 && (
                  <Badge className="bg-red-500 text-white ml-1">{pendingErrors.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="questions" className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Întrebări ({questions.length})
                {pendingQuestions.length > 0 && (
                  <Badge className="bg-amber-500 text-white ml-1">{pendingQuestions.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Errors Tab */}
            <TabsContent value="errors">
              <ScrollArea className="h-[400px]">
                {errors.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>Nu există erori raportate</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {errors.map((error, idx) => (
                      <div 
                        key={error.id} 
                        className={`p-4 rounded-lg border ${
                          error.resolved 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={error.resolved ? 'bg-green-500' : 'bg-red-500'}>
                                #{idx + 1}
                              </Badge>
                              {error.resolved ? (
                                <Badge variant="outline" className="text-green-600 border-green-300">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Rezolvat
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-red-600 border-red-300">
                                  <Clock className="w-3 h-3 mr-1" />
                                  În așteptare
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-slate-800 mb-3">{error.description}</p>
                            
                            <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {error.company_name || 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {error.user_name || error.user_email || 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(error.created_at)}
                              </span>
                            </div>
                          </div>
                          
                          {!error.resolved && (
                            <Button
                              size="sm"
                              onClick={() => markErrorResolved(error.id)}
                              className="bg-green-500 hover:bg-green-600"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Rezolvat
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Questions Tab */}
            <TabsContent value="questions">
              <ScrollArea className="h-[400px]">
                {questions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>Nu există întrebări înregistrate</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {questions.map((question, idx) => (
                      <div 
                        key={question.id} 
                        className={`p-4 rounded-lg border ${
                          question.needs_followup && !question.admin_reply
                            ? 'bg-amber-50 border-amber-200' 
                            : question.admin_reply
                            ? 'bg-green-50 border-green-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">#{idx + 1}</Badge>
                              {question.admin_reply ? (
                                <Badge className="bg-green-500">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Răspuns trimis
                                </Badge>
                              ) : question.has_response ? (
                                <Badge className="bg-blue-500">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Răspuns AI
                                </Badge>
                              ) : question.needs_followup ? (
                                <Badge className="bg-amber-500">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Necesită răspuns
                                </Badge>
                              ) : (
                                <Badge variant="secondary">Procesată</Badge>
                              )}
                            </div>
                            
                            <p className="text-slate-800 mb-3 font-medium">{question.question}</p>
                            
                            {/* Show admin reply if exists */}
                            {question.admin_reply && (
                              <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-3">
                                <p className="text-xs text-green-700 font-semibold mb-1 flex items-center gap-1">
                                  <Reply className="w-3 h-3" />
                                  Răspunsul tău:
                                </p>
                                <p className="text-sm text-green-800">{question.admin_reply}</p>
                                {question.replied_at && (
                                  <p className="text-xs text-green-600 mt-1">
                                    Trimis la: {formatDate(question.replied_at)}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {question.company_name || 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {question.user_name || question.user_email || 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {question.user_email || 'N/A'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(question.created_at)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Reply Button */}
                          <Button
                            size="sm"
                            onClick={() => openReplyDialog(question)}
                            className={question.admin_reply 
                              ? "bg-slate-500 hover:bg-slate-600" 
                              : "bg-amber-500 hover:bg-amber-600"
                            }
                          >
                            <Reply className="w-4 h-4 mr-1" />
                            {question.admin_reply ? 'Editează' : 'Răspunde'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Reply Dialog */}
      <Dialog open={showReplyDialog} onOpenChange={setShowReplyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Reply className="w-5 h-5 text-amber-500" />
              Răspunde la Întrebare
            </DialogTitle>
            <DialogDescription>
              Răspunsul va fi salvat și trimis către firmă
            </DialogDescription>
          </DialogHeader>
          
          {selectedQuestion && (
            <div className="space-y-4">
              {/* Question info */}
              <div className="bg-slate-50 rounded-lg p-3 border">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                  <Building2 className="w-3 h-3" />
                  <span>{selectedQuestion.company_name || 'N/A'}</span>
                  <span className="mx-1">•</span>
                  <User className="w-3 h-3" />
                  <span>{selectedQuestion.user_name || selectedQuestion.user_email}</span>
                </div>
                <p className="text-slate-800 font-medium">{selectedQuestion.question}</p>
              </div>
              
              {/* Reply textarea */}
              <div className="space-y-2">
                <Label htmlFor="reply">Răspunsul tău</Label>
                <Textarea
                  id="reply"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Scrie răspunsul pentru firmă..."
                  rows={5}
                  className="resize-none"
                />
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowReplyDialog(false)}
                  disabled={sendingReply}
                >
                  Anulează
                </Button>
                <Button 
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyText.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {sendingReply ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Se trimite...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Trimite Răspuns
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
